import * as SVG from 'svg.js';

import { AfterContentInit, Component, ElementRef, EventEmitter, HostListener,
         Input, OnChanges, Output, SimpleChanges, ViewChild
       } from '@angular/core';
import { StageConstants } from 'app/core/constants';
import { Utils } from 'core/utils';
import { TextPlaceholder
       } from '../../collage-designer/stage/placeholder/text-placeholder';
import { scaleModel, Text, TextChunk } from '../../text/text.model';

interface CopiedDocumentElements
{
  document: SVG.Doc;
  content: SVG.G;
  text: SVG.Text;
}

@Component({
  selector: 'placeholder-textarea',
  templateUrl: './placeholder-textarea.component.html',
  styleUrls: [ './placeholder-textarea.component.scss' ]
})
export class PlaceholderTextareaComponent implements AfterContentInit, OnChanges
{
  @Input('textModel') public textModel: Text | undefined = undefined;
  @Input('placeholder') public placeholder: TextPlaceholder;
  @Output('textChange') public textChange: EventEmitter<Array<TextChunk>> =
    new EventEmitter<Array<TextChunk>>();

  public displayModel: Text | undefined = undefined;
  public textOffsetLeft: string = '0px';
  public textOffsetTop: string = '0px';

  @ViewChild('background') private background: ElementRef;
  private backgroundTextWrapper: SVG.Doc | undefined;
  private backgroundScaleFactor: number = 1;

  constructor(private hostElement: ElementRef)
  {}

  public ngOnChanges(changes: SimpleChanges): void
  {
    if ('placeholder' in changes) {
      this.catchBackgroundDocument();
      this.updateBackground();
    }

    if ('textModel' in changes) {
      this.updateTextModel();
    }
  }

  public onTextChanged(chunks: Array<TextChunk>): void
  {
    this.textChange.emit(chunks);
  }

  private catchBackgroundDocument(): void
  {
    if (! this.placeholder) {
      return;
    }

    const copied = this.clonePlaceholderDocument(
        this.placeholder,
        SVG.adopt(this.background.nativeElement) as SVG.Doc);

    this.backgroundTextWrapper = (<any>copied.text).parent() as SVG.Doc;
    const rotation = this.getRotation(this.backgroundTextWrapper);
    this.addShadowOverlay(
      this.backgroundTextWrapper, copied.content, copied.document, rotation);
    copied.content.rotate(-rotation);
    this.setBackgroundViewbox(this.backgroundTextWrapper, copied.document);
    copied.text.hide();
  }

  private getRotation(textWrapper: SVG.Doc): number
  {
    return (<any>textWrapper).parent().transform().rotation;
  }

  private addShadowOverlay(
    textWrapper: SVG.Doc, content: SVG.G, backgroundDocument: SVG.Doc,
    rotation: number): void
  {
    const { x, y, width, height } = backgroundDocument.bbox();
    const diagonal  = Math.sqrt(Math.pow(width, 2) + Math.pow(height, 2));
    const docBox = {
      x: x - diagonal / 2,
      y: y - diagonal / 2,
      width: diagonal * 2,
      height: diagonal * 2,
    };

    const wrapperRbox = (<any>textWrapper).rbox(backgroundDocument);
    const wrapperBox = {
      x: wrapperRbox.x + wrapperRbox.width / 2
          - textWrapper.viewbox().width / 2,
      y: wrapperRbox.y + wrapperRbox.height / 2
          - textWrapper.viewbox().height / 2,
      width: textWrapper.viewbox().width,
      height: textWrapper.viewbox().height
    };

    const pathData = (
      `M ${docBox.x} ${docBox.y} ` +
      `h ${docBox.width} ` +
      `v ${docBox.height} ` +
      `h -${docBox.width} ` +
      `v -${docBox.height} ` +
      `M ${wrapperBox.x} ${wrapperBox.y} ` +
      `l 0 ${wrapperBox.height} ` +
      `l ${wrapperBox.width} 0 ` +
      `l 0 ${-wrapperBox.height} ` +
      `l ${-wrapperBox.width} 0 ` +
      `z`
    );

    content.path(pathData).fill('rgba(255, 255, 255, 0.6)').rotate(
        rotation,
        (wrapperBox.x + wrapperBox.width / 2),
        (wrapperBox.y + wrapperBox.height / 2));
  }

  private setBackgroundViewbox(
    textWrapper: SVG.Doc, backgroundDocument: SVG.Doc): void
  {
    const PADDING_PERCENT = 10;

    const box = (<any>textWrapper).rbox(backgroundDocument);
    const padding = box.width * (PADDING_PERCENT / 100);

    box.x -= padding / 2;
    box.y -= padding / 2;
    box.width += padding;
    box.height += padding;

    backgroundDocument.viewbox(box);
  }

  private clonePlaceholderDocument(
    placeholder: TextPlaceholder, target: SVG.Doc): CopiedDocumentElements
  {
    const originalText = placeholder.element as SVG.Element;
    const originalDocument = (<any>originalText).doc();

    originalText.data('original-id', originalText.id());

    const contentGroup = this.copyDocument(originalDocument, target);

    const backgroundText = (<any>target)
      .select(`[data-original-id=\"${originalText.id()}\"]`)
      .first() as SVG.Text;

    originalText.data('original-id', null); // tslint:disable-line

    return {
      document: target,
      content: contentGroup,
      text: backgroundText,
    };
  }

  private copyDocument(source: SVG.Doc, target: SVG.Doc): SVG.G
  {
    const children = source.children() as Array<SVG.Element>;

    target.clear();
    const content = target.group();

    const nonIgnoredChildren = children
      .filter(c => !(StageConstants.DESIGNER_ONLY_CLASSES
                     .some(el => c.hasClass(el))));

    for (const child of nonIgnoredChildren) {
      (<any>child).clone(content);
    }

    return content;
  }

  public ngAfterContentInit(): void {
    if (! Utils.isSmallDevice()) {
      return;
    }

    this.updateTextPosition();
  }

  @HostListener('window:resize', [])
  private updateBackground(): void
  {
    this.updateTextPosition();
    this.updateBackgroundScaleFactor();
    this.updateTextModel();
  }

  private updateTextPosition(): void
  {
    if (! this.backgroundTextWrapper) {
      return;
    }

    const origin = this.hostElement.nativeElement.getBoundingClientRect();
    const box = this.backgroundTextWrapper.node.getBoundingClientRect();

    this.textOffsetLeft = `${box.left - origin.left}px`;
    this.textOffsetTop = `${box.top - origin.top}px`;
  }

  private updateBackgroundScaleFactor(): void
  {
    if (! this.backgroundTextWrapper) {
      return;
    }

    // This calculation warrants some explanation.
    //
    // - Let f, m and g be functions that transform coordinate systems:
    //   - f translates template coordinates to points.
    //   - m translates template coordinates to screen coordinates.
    //   - g translates points to screen coordinates.
    //   — we are looking for g.
    //
    // - Let T be the "template native" coordinate system.
    //     (i.e. the one used by the background SVG.)
    //
    // - Let P be the "typographic point" coordinate system.
    //
    // - Let S be the "screen" coordinate system.
    //
    // - the signatures of f,g and m are:
    //   - f: T → P
    //   - m: T → S
    //   - g: P → S
    //
    // - To find g we can express it as a composition of m and f:
    //   - g¯¹ = f . m¯¹; (S → P) = (T -> P) . (S → T)
    //   ⇒ g = (f . m¯¹)¯¹ = f¯¹ . m
    //
    // - Identify the functions:
    //   - f is placeholder.toDisplayFontSize. Its inverse is
    //       placeholder.toTemplateFontSize
    //   - m is the ratio of the placeholder height "on screen" to its height
    //       in its own coordinate system.
    //   - g is the backgroundScaleFactor

    const bounds = this.backgroundTextWrapper
      .select('rect')
      .first() as SVG.Rect;

    this.backgroundScaleFactor =
      this.placeholder.toTemplateFontSize(
        bounds.rbox().height / this.backgroundTextWrapper.height());
  }

  private updateTextModel(): void
  {
    if (! this.textModel) {
      return;
    }

    this.displayModel = scaleModel(this.textModel, this.backgroundScaleFactor);
  }
}



// WEBPACK FOOTER //
// ./src/app/controls/text-editing/placeholder-textarea.component.ts